import React from 'react';
import {
  WhiteInstantSearchBuilder,
  mappers,
  RefinementListPanel,
  MenuSelectPanel,
  MenuRadioPanel,
  RadixUIPanelRangeSlider,
  CurrentRefinements,
  Pagination,
  NoResults,
  SortBy,
  SearchBox,
  HierarchicalMenuPanel,
} from '@white-nl/instantsearch-components';

import { CheckSharpSolid, MagnifyingGlassSharpRegular, XmarkSharpRegular } from '../components/ui/icons';
import { Hit } from '../components/catalog/results/Hit';
import Loading from '../components/catalog/results/Loading';
import { ClearIndicator, DropdownIndicator } from '../components/ui/React-select';
import Stats from '../components/ui/metadata/Stats';

const settings = document.getElementById('section-filters')?.dataset || {};
const whiteInstantSearch = new WhiteInstantSearchBuilder();
if (settings) {
  const graphqlUrl = `${settings.nodeUrl}api/categories/products`;
  fetch(graphqlUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ siteId: settings.siteId }),
  }).then((res) => res.json())
    .then((categories) => {
      const categoryLabelMapping: any = {};
      const categoryDisplaySequenceMapping: any = {};
      const categoryHasChildrenMapping: any = {};
      categories.overview.forEach(
        (category: {
          __typename: string;
          uri: string;
          title: string;
        }) => {
          categoryLabelMapping[category.uri] = category.title;
          categoryDisplaySequenceMapping[category.uri] = 0;
        },
      );
      categories.entries.forEach(
        (category: {
          __typename: string;
          slug: string;
          title: string;
          displaySequence: number;
          parent: {
            slug: string;
          };
        }) => {
          categoryLabelMapping[category.slug] = category.title;
          categoryDisplaySequenceMapping[category.slug] = category.displaySequence ?? 999999;
          if (category.parent) {
            categoryHasChildrenMapping[category.parent.slug] = 1;
          }
        },
      );

      const transformMenuItems = (items: any) => items.map((item: any) => {
        if (item.data && item.data.length > 0) {
          return Object.assign(item, { data: transformMenuItems(item.data) });
        }

        const hasChildren = !!categoryHasChildrenMapping[item.label];

        return Object.assign(item, { data: (hasChildren ? [] : null) });
      });
      whiteInstantSearch
        .withSearchkit(
          settings.indexName || '',
          settings.publicUrl || '',
          settings.jwt || '',
          settings.refreshUrl || '',
        )
        .withWhiteInstantSearchRouting({
          baseUrl: settings.baseUrl,
          pathAttribute: 'categories.lvl1',
          addToPath: true,
          addTrailingSlashesToUrls: settings.trailingSlashes === '1',
          writeDelay: 0,
        })
        .addHierarchicalMenu(
          [
            {
              settings: {
                attributes: [
                  'categories.lvl1',
                  'categories.lvl2',
                  'categories.lvl3',
                  'categories.lvl4',
                  'categories.lvl5',
                ],
                classNames: {
                  root: 'filters-list',
                  count: 'filters-count',
                  selectedItem: 'active',
                },
                sortBy: (a, b) => {
                  const titleA = categoryDisplaySequenceMapping[a.name];
                  const titleB = categoryDisplaySequenceMapping[b.name];
                  if (titleA > titleB) {
                    return 1;
                  }
                  if (titleA < titleB) {
                    return -1;
                  }

                  return 0;
                },
                separator: '/',
                hardRefresh: true,
                transformItems: (items) => {
                  const transformed = transformMenuItems(items);
                  return mappers.transformers.mapLabels(categoryLabelMapping)(transformed);
                },
                limit: 50,
                panelSettings: {
                  collapsiblePanelSettings: {
                    classNames: {
                      root: 'form-wrap form-wrap--with-margin collapsible-panel--category-menu',
                      header: 'filter-title h4',
                    },
                    defaultExpanded: true,
                    canCollapse: true,
                  },
                  header: Craft.t('catalog', 'Products'),
                },
              },
              component: HierarchicalMenuPanel,
            },
          ],
          '#white-instant-search-hierarchical-menu',
        )
        .addSearchingComponent([
          {
            settings: {
              classNames: {
                submit: 'submit',
                reset: 'submit',
                loadingIndicator: 'submit',
                input: 'form-control',
              },
              submitIconComponent: () => (
                <MagnifyingGlassSharpRegular />
              ),
              resetIconComponent: () => (
                <XmarkSharpRegular />
              ),
              placeholder: Craft.t('catalog', 'Find your product'),
              translations: {
                submitButtonTitle: Craft.t('catalog', 'Submit the search query'),
                resetButtonTitle: Craft.t('catalog', 'Clear the search query'),
              },
            },
            component: SearchBox,
          },
        ])
        .setHitsPerPage(18)
        .addAdditionalData({ hitsPerPage: 18 })
        .addHitComponent([
          {
            settings: {
              loading: {
                translations: {
                  loading: Craft.t('catalog', 'Loading products...'),
                },
              },
              noResults: {
                translations: {
                  noResults: Craft.t('catalog', 'Sorry, we can\'t find any products!'),
                  refinements: Craft.t('catalog', 'Try to reset your applied filters.'),
                  query: Craft.t('catalog', 'Please try another query.'),
                },
              },
            },
            component: Hit,
            loadingComponent: Loading,
            noResultsComponent: NoResults,
          },
        ])
        .addStatsComponent([
          {
            settings: {
              translations: {
                resultsFound: Craft.t('catalog', 'results found'),
                resultFound: Craft.t('catalog', 'result found'),
              },
            },
            component: Stats,
          },
        ])
        .addPaginationComponent([
          {
            settings: {
              showFirst: false,
              showLast: false,
              classNames: {
                list: 'pagination',
                item: 'page-item',
                previousPageItem: 'previous',
                selectedItem: 'active',
                disabledItem: 'disabled',
                nextPageItem: 'next',
                link: 'page-link',
              },
              translations: {
                previousPageItemText: Craft.t('pagination', 'Previous'),
                previousPageItemAriaLabel: Craft.t('pagination', 'Go to previous page'),
                nextPageItemText: Craft.t('pagination', 'Next'),
                nextPageItemAriaLabel: Craft.t('pagination', 'Go to next page'),
              },
            },
            component: Pagination,
          },
        ])
        .addCurrentRefinementsComponent(
          [{
            settings: {
              excludedAttributes: ['categories.lvl1'],
            },
            component: ({ currentRefinements }) => (
              currentRefinements.items.reduce((a, b) => a + b.refinements.length, 0) > 0
              && (<CheckSharpSolid iconClass="check-icon" />)
            ),
          }],
          '.filters-icon',
        )
        .addStatsComponent([{ component: ({ stats }) => (`(${stats.nbHits})`) }], '.hits-count')
        .setScrolling({
          includeAttributes: ['page'],
        })
        .addSortByComponent([
          {
            settings: {
              items: [
                { label: Craft.t('catalog', 'Most relevant first'), value: settings.indexName || '' },
                { label: Craft.t('catalog', 'Newest first'), value: `${settings.indexName || ''}_date_desc` },
                { label: Craft.t('catalog', 'Alphabet A-Z'), value: `${settings.indexName || ''}_title_asc` },
              ],
              unstyled: true,
              components: {
                DropdownIndicator,
              },
            },
            component: SortBy,
          },
        ]);

      const currentRefinementsTranslations: string[] = [];
      const {
        productAttributes,
      } = settings;

      if (productAttributes) {
        const parsedProductAttributes = JSON.parse(productAttributes ?? '');

        parsedProductAttributes.forEach((attr: any) => {
          currentRefinementsTranslations[attr.externalId] = attr.title;
          if (attr.catalogFilterType === 'refinementList') {
            whiteInstantSearch.addRefinementList([
              {
                settings: {
                  attribute: attr.externalId,
                  classNames: {
                    label: 'custom-control custom-checkbox',
                    checkbox: 'custom-control-input',
                    labelText: 'custom-control-label',
                    showMore: 'btn btn-link btn-sm',
                    disabledShowMore: 'd-none',
                    count: 'filters-count',
                    searchBox: 'filter-search-wrap',
                  },
                  searchable: attr.catalogShowSearchbox,
                  showMore: attr.catalogEnableShowMore,
                  // limit: 2,
                  sortBy: JSON.parse(attr.catalogOptionSorting),
                  translations: {
                    showMoreButtonText({ isShowingMore }) {
                      return isShowingMore ? Craft.t('catalog', 'Show less') : Craft.t('catalog', 'Show more');
                    },
                    submitButtonTitle: Craft.t('catalog', 'Submit the search query'),
                    resetButtonTitle: Craft.t('catalog', 'Clear the search query'),
                  },
                  panelSettings: {
                    collapsiblePanelSettings: {
                      classNames: {
                        root: 'form-wrap',
                        header: 'filter-title h4',
                      },
                      defaultExpanded: attr.catalogExpandedByDefault,
                      canCollapse: attr.catalogCanCollapse,
                    },
                    header: attr.title,
                  },
                },
                component: RefinementListPanel,
              },
            ]);
          }
          if (attr.catalogFilterType === 'menuSelect') {
            whiteInstantSearch.addMenu([
              {
                settings: {
                  attribute: attr.externalId,
                  sortBy: JSON.parse(attr.catalogOptionSorting),
                  classNames: {
                    control: () => 'form-select',
                    menu: () => 'form-select-list',
                  },
                  isSearchable: attr.catalogShowSearchbox,
                  unstyled: true,
                  components: {
                    ClearIndicator,
                    DropdownIndicator,
                  },
                  panelSettings: {
                    collapsiblePanelSettings: {
                      classNames: {
                        root: 'form-wrap',
                        header: 'filter-title h4',
                      },
                      defaultExpanded: attr.catalogExpandedByDefault,
                      canCollapse: attr.catalogCanCollapse,
                    },
                    header: attr.title,
                  },
                },
                component: MenuSelectPanel,
              },
            ]);
          }
          if (attr.catalogFilterType === 'menuRadio') {
            whiteInstantSearch.addMenu([
              {
                settings: {
                  attribute: attr.externalId,
                  classNames: {
                    item: 'custom-control custom-radio',
                    radio: 'custom-control-input',
                    label: 'custom-control-label',
                    showMore: 'btn btn-link btn-sm',
                    disabledShowMore: 'd-none',
                    count: 'filters-count',
                  },
                  showMore: attr.catalogEnableShowMore,
                  // limit: 1,
                  sortBy: JSON.parse(attr.catalogOptionSorting),
                  translations: {
                    showMoreButtonText({ isShowingMore }) {
                      return isShowingMore ? Craft.t('catalog', 'Show less') : Craft.t('catalog', 'Show more');
                    },
                  },
                  panelSettings: {
                    collapsiblePanelSettings: {
                      classNames: {
                        root: 'form-wrap',
                        header: 'filter-title h4',
                      },
                      defaultExpanded: attr.catalogExpandedByDefault,
                      canCollapse: attr.catalogCanCollapse,
                    },
                    header: attr.title,
                  },
                },
                component: MenuRadioPanel,
              },
            ]);
          }
          if (attr.catalogFilterType === 'rangeSlider') {
            whiteInstantSearch.addRangeSlider([
              {
                settings: {
                  attribute: attr.externalId,
                  classNames: {
                    root: 'range-slider',
                  },
                  step: 5,
                  minStepsBetweenThumbs: 1,
                  showThumbValues: true,
                  panelSettings: {
                    collapsiblePanelSettings: {
                      classNames: {
                        root: 'form-wrap',
                        header: 'filter-title h4',
                      },
                      defaultExpanded: attr.catalogExpandedByDefault,
                      canCollapse: attr.catalogCanCollapse,
                    },
                    header: attr.title,
                  },
                },
                component: RadixUIPanelRangeSlider,
              },
            ]);
          }
        });
      }
      whiteInstantSearch
        .addCurrentRefinementsComponent([
          {
            settings: {
              excludedAttributes: ['categories.lvl1', 'query'],
              translations: currentRefinementsTranslations,
              clearRefinementsSettings: {
                translations: {
                  clearAll: Craft.t('catalog', 'Clear all'),
                },
              },
            },
            component: CurrentRefinements,
          },
        ])
        .where(`siteId:${settings.siteId} AND isAvailableInSiteRegion:true`)
        .build()
        .show();
    });
}
